import { GridSortModel } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { fetchRankedCalls } from "../../../data/queries/artifacts";
import type { Artifact } from "../../../types/artifact";
import type { RankingResultCount } from "../../../types/callRanking";
import { ALL_AGENTS, DEFAULT_RANKING_COUNT, RANKING_ORDERS, RankingOrder } from "../../../types/callRanking";
import { isValidRankingResultCount } from "../../../utils/callRanking";
import { getTimeRangeDbFilters } from "../../../utils/timerange";

export interface RankedCallsParams {
  order: RankingOrder;
  lens: string;
  attribute?: string;
  count: RankingResultCount;
  timeRange: string;
  agent?: string;
}

interface UseRankedCallsModeParams {
  permittedEmails: string[] | null;
}

const DEFAULT_PAGE_SIZE = 500;

const isValidOrder = (value: string): value is RankingOrder =>
  (RANKING_ORDERS as readonly string[]).includes(value);

export interface UseRankedCallsModeResult {
  isActive: boolean;
  rows: Artifact[];
  rowCount: number;
  loading: boolean;
  error: Error | null;
  sortModel: GridSortModel;
  pageSize: number;
  refetch: () => void;
  rankedCallsParams: RankedCallsParams | null;
}

const parseRankedCallsParams = (searchParams: URLSearchParams): RankedCallsParams | null => {
  const order = searchParams.get("ranking_order");
  const lens = searchParams.get("sc_name");

  if (!order || !isValidOrder(order) || !lens) {
    return null;
  }

  const attribute = searchParams.get("ranking_attribute") || undefined;
  const countParam = searchParams.get("ranking_count");
  const rawCount = countParam ? parseInt(countParam, 10) : DEFAULT_RANKING_COUNT;
  const count: RankingResultCount = isValidRankingResultCount(rawCount) ? rawCount : DEFAULT_RANKING_COUNT;
  const timeRange = searchParams.get("time_range") || "last7Days";
  const agent = searchParams.get("user_email") || undefined;

  return {
    order,
    lens,
    attribute,
    count,
    timeRange,
    agent: agent !== ALL_AGENTS ? agent : undefined
  };
};

const buildSortModel = (params: RankedCallsParams | null): GridSortModel => {
  if (!params) {
    return [{ field: "created", sort: "desc" }];
  }

  const sortDirection = params.order === RankingOrder.BestFirst ? "desc" : "asc";
  const sortField = params.attribute ? "attribute_score" : "sc_overall_score";

  return [{ field: sortField, sort: sortDirection }];
};

export const useRankedCallsMode = ({ permittedEmails }: UseRankedCallsModeParams): UseRankedCallsModeResult => {
  const [searchParams] = useSearchParams();

  const rankedCallsParams = useMemo(
    () => parseRankedCallsParams(searchParams),
    [searchParams]
  );

  const isActive = rankedCallsParams !== null;

  const timeFilters = useMemo(() => {
    if (!rankedCallsParams) {
      return { created_from: undefined, created_to: undefined };
    }
    return getTimeRangeDbFilters(rankedCallsParams.timeRange);
  }, [rankedCallsParams]);

  const { data: rankedData, isFetching, error, refetch } = useQuery({
    enabled: isActive,
    queryKey: ["rankedCalls", rankedCallsParams, permittedEmails],
    queryFn: () =>
      fetchRankedCalls({
        lensName: rankedCallsParams!.lens,
        order: rankedCallsParams!.order,
        attributeName: rankedCallsParams!.attribute,
        startDate: timeFilters.created_from,
        endDate: timeFilters.created_to,
        agentEmail: rankedCallsParams!.agent,
        resultLimit: rankedCallsParams!.count,
        permittedEmails
      }),
    staleTime: 0,
    gcTime: 0
  });

  const rows: Artifact[] = rankedData?.data ?? [];
  const rowCount = rows.length;

  const sortModel = useMemo(
    () => buildSortModel(rankedCallsParams),
    [rankedCallsParams]
  );

  const pageSize = rankedCallsParams?.count ?? DEFAULT_PAGE_SIZE;

  return {
    isActive,
    rows,
    rowCount,
    loading: isFetching,
    error: error ?? rankedData?.error ?? null,
    sortModel,
    pageSize,
    refetch,
    rankedCallsParams
  };
};
